import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  Clock,
  FolderTree as FolderTreeIcon,
  Search,
  X,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { FolderTree } from "@/components/dms/FolderTree";
import { foldersApi } from "@/api/folders";
import { documentsApi } from "@/api/documents";
import { DocumentPreviewPane, type PreviewDocument } from "@/components/review/DocumentPreview";
import type {
  DocumentDto,
  DocumentFolderTreeDto,
  IngestionStatus,
} from "@/types/api";

// LLM & Reports v3.6 — repository-backed supporting-documents picker.
//
// Reuses the canonical document repository seams the /documents app uses —
// the server-side folder search (GET /api/folders/search via
// foldersApi.searchFolders), the shared <FolderTree>, folder-contents
// (foldersApi.detail), and the document preview/download endpoints — so the
// report wizard browses the full repository (every entity category) with
// search and a preview pane, rather than a bare per-entity tree. There is no
// second search implementation: this is a consumer of the same seam.
//
// Selection mode is the additive part: rows render a checkbox, a sticky
// selection summary, and an explicit "Add to report" action that attaches
// the chosen documents *by reference* (no re-upload).

interface RepositoryDocumentPickerProps {
  open: boolean;
  onClose: () => void;
  /** Documents already attached to the report — rendered as added / locked. */
  attachedDocumentIds: string[];
  /** Remaining attachment slots (MaxAttachments − current count). */
  remainingSlots: number;
  /** Import the chosen documents. Resolves once attached so we can close. */
  onAdd: (documentIds: string[]) => Promise<void>;
  adding?: boolean;
}

export function RepositoryDocumentPicker({
  open,
  onClose,
  attachedDocumentIds,
  remainingSlots,
  onAdd,
  adding = false,
}: RepositoryDocumentPickerProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeRootId, setActiveRootId] = useState<string | null>(null);
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);
  const [previewDocId, setPreviewDocId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const attachedSet = useMemo(
    () => new Set(attachedDocumentIds),
    [attachedDocumentIds],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Canonical server-side folder search — the single shared seam.
  const browse = useQuery({
    queryKey: ["repo-picker", "search", activeCategory, debouncedSearch],
    queryFn: () =>
      foldersApi.searchFolders({
        category: activeCategory,
        q: debouncedSearch || null,
        sort: "name",
        dir: "asc",
        pageSize: 50,
      }),
    enabled: open,
  });

  // Drill into a selected root with the shared tree component.
  const tree = useQuery({
    queryKey: ["repo-picker", "tree", activeRootId],
    queryFn: () => foldersApi.tree(activeRootId!),
    enabled: open && !!activeRootId,
  });

  // Documents in the active folder.
  const detail = useQuery({
    queryKey: ["repo-picker", "folder", activeFolderId],
    queryFn: () => foldersApi.detail(activeFolderId!),
    enabled: open && !!activeFolderId,
  });

  const docs = detail.data?.documents ?? [];

  const atCapacity = selected.size >= remainingSlots;

  const toggle = (doc: DocumentDto) => {
    if (attachedSet.has(doc.id) || !isRepositoryReady(doc)) return;
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(doc.id)) {
        next.delete(doc.id);
      } else if (next.size < remainingSlots) {
        next.add(doc.id);
      }
      return next;
    });
  };

  const reset = () => {
    setSelected(new Set());
    setPreviewDocId(null);
  };

  const handleAdd = async () => {
    if (selected.size === 0) return;
    await onAdd([...selected]);
    reset();
  };

  const handleSelectFromPreview = async (doc: DocumentDto) => {
    if (attachedSet.has(doc.id) || adding || remainingSlots <= 0 || !isRepositoryReady(doc)) {
      return;
    }
    await onAdd([doc.id]);
    setSelected((cur) => {
      const next = new Set(cur);
      next.delete(doc.id);
      return next;
    });
  };

  const handleRemoveFromPreview = (doc: DocumentDto) => {
    setSelected((cur) => {
      const next = new Set(cur);
      next.delete(doc.id);
      return next;
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Browse repository"
      description="Search every entity category, preview a document, and pull one or more into this report."
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-space-3">
          <div className="font-body text-caption text-gray-mid">
            {selected.size} document{selected.size === 1 ? "" : "s"} selected
            {atCapacity && (
              <span className="ml-space-2 text-status-warning">
                Attachment limit reached ({remainingSlots} slot
                {remainingSlots === 1 ? "" : "s"} left).
              </span>
            )}
          </div>
          <div className="flex items-center gap-space-2">
            <Button variant="secondary" onClick={handleClose} disabled={adding}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleAdd()}
              disabled={selected.size === 0 || adding}
            >
              {adding ? "Adding…" : `Add to report`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 gap-space-3 md:grid-cols-12">
        {/* Browse column — search + categories + roots + tree */}
        <aside className="md:col-span-4 flex flex-col gap-space-2">
          <div className="relative">
            <Search
              size={14}
              aria-hidden
              className="absolute left-space-2 top-1/2 -translate-y-1/2 text-gray-mid"
            />
            <Input
              type="search"
              placeholder="Search documents & folders…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-space-5"
            />
          </div>

          {(browse.data?.categories?.length ?? 0) > 0 && (
            <div className="flex flex-wrap gap-space-1">
              {browse.data!.categories.map((c) => (
                <button
                  key={c.category}
                  type="button"
                  onClick={() =>
                    setActiveCategory((cur) =>
                      cur === c.category ? null : c.category,
                    )
                  }
                  className={`rounded-full border px-space-2 py-[2px] text-caption ${
                    activeCategory === c.category
                      ? "border-gold bg-gold-highlight/10 text-onyx"
                      : "border-border-light text-gray-mid hover:bg-white-warm"
                  }`}
                >
                  {c.category} ({c.count})
                </button>
              ))}
            </div>
          )}

          <div className="max-h-[22rem] overflow-auto rounded-md border border-border-light bg-white-warm p-space-2">
            {browse.isLoading ? (
              <p className="text-caption text-gray-mid">Loading sources…</p>
            ) : (browse.data?.hits.data.length ?? 0) === 0 ? (
              <p className="text-caption text-gray-mid">No matching folders.</p>
            ) : (
              <ul className="flex flex-col gap-[2px]">
                {browse.data!.hits.data.map((root) => (
                  <li key={root.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveRootId(root.id);
                        setActiveFolderId(root.id);
                      }}
                      className={`flex w-full items-center gap-space-2 rounded-md px-space-2 py-space-1 text-left text-small ${
                        activeRootId === root.id
                          ? "bg-white-pure font-semibold text-onyx"
                          : "text-gray-dark hover:bg-white-pure"
                      }`}
                    >
                      <FolderTreeIcon size={14} aria-hidden className="shrink-0" />
                      <span className="truncate">{root.name}</span>
                      <span className="ml-auto text-caption text-gray-mid">
                        {root.documentCount}
                      </span>
                    </button>
                    {activeRootId === root.id && tree.data && (
                      <div className="ml-space-2 mt-[2px] border-l border-border-light pl-space-2">
                        <FolderTree
                          nodes={[tree.data]}
                          selectedId={activeFolderId}
                          onSelect={(n: DocumentFolderTreeDto) =>
                            setActiveFolderId(n.id)
                          }
                          initiallyExpanded
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Document list column */}
        <section className="md:col-span-8 max-h-[26rem] overflow-auto rounded-md border border-border-light bg-white-pure p-space-2">
          {!activeFolderId ? (
            <EmptyHint icon={<FolderTreeIcon size={24} aria-hidden />}>
              Pick a folder to list its documents.
            </EmptyHint>
          ) : detail.isLoading ? (
            <p className="text-caption text-gray-mid">Loading documents…</p>
          ) : docs.length === 0 ? (
            <p className="text-caption text-gray-mid italic">
              No documents in this folder.
            </p>
          ) : (
            <ul className="flex flex-col gap-[2px]">
              {docs.map((d) => {
                const added = attachedSet.has(d.id);
                const checked = selected.has(d.id);
                const blocked = !checked && !added && (atCapacity || !isRepositoryReady(d));
                return (
                  <li key={d.id}>
                    <div
                      className={`flex items-center gap-space-2 rounded-md px-space-2 py-space-1 ${
                        previewDocId === d.id ? "bg-white-warm" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked || added}
                        disabled={added || blocked}
                        onChange={() => toggle(d)}
                        aria-label={`Select ${d.title}`}
                        className="h-4 w-4 accent-gold"
                      />
                      <button
                        type="button"
                        onClick={() => setPreviewDocId(d.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <div className="truncate text-small font-semibold text-onyx">
                          {d.title}
                        </div>
                        <div className="truncate text-caption text-gray-mid">
                          {d.fileName}
                        </div>
                      </button>
                      {added ? (
                        <span className="text-caption text-status-success">
                          Selected ✓
                        </span>
                      ) : (
                        <RepositoryBadge status={d.ingestionStatus} />
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {previewDocId && (
          <PreviewDrawer
            doc={docs.find((d) => d.id === previewDocId) ?? null}
            documentId={previewDocId}
            added={attachedSet.has(previewDocId)}
            selected={selected.has(previewDocId)}
            atCapacity={remainingSlots <= 0}
            adding={adding}
            onClose={() => setPreviewDocId(null)}
            onSelect={(doc) => void handleSelectFromPreview(doc)}
            onRemove={handleRemoveFromPreview}
          />
        )}
      </div>
    </Modal>
  );
}

function EmptyHint({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-space-2 py-space-5 text-center text-caption text-gray-mid">
      {icon}
      <p>{children}</p>
    </div>
  );
}

function PreviewDrawer({
  doc,
  documentId,
  added,
  selected,
  atCapacity,
  adding,
  onClose,
  onSelect,
  onRemove,
}: {
  doc: DocumentDto | null;
  documentId: string;
  added: boolean;
  selected: boolean;
  atCapacity: boolean;
  adding: boolean;
  onClose: () => void;
  onSelect: (doc: DocumentDto) => void;
  onRemove: (doc: DocumentDto) => void;
}) {
  const preview = useQuery({
    queryKey: ["repo-picker", "preview", documentId],
    queryFn: () => documentsApi.preview(documentId),
  });
  const url = preview.data?.sasUrl ?? null;
  const ready = doc ? isRepositoryReady(doc) : false;
  const disabledReason = !doc
    ? "Document metadata is still loading."
    : !ready
      ? `Repository indexing is ${doc.ingestionStatus.toLowerCase()}; wait until it is ready before adding to a report.`
      : atCapacity && !added && !selected
        ? "The report has reached the 50-document attachment limit."
        : null;

  const previewDocument: PreviewDocument = {
    title: doc?.title ?? "Document",
    fileName: doc?.fileName ?? null,
    contentType: doc?.contentType ?? null,
    url,
    isPdf: preview.data?.isPdf,
    state: preview.isLoading ? "loading" : url ? "ready" : "unavailable",
    message: preview.isError
      ? "Preview not available for this file type — download or open in a new tab to view."
      : "Preview not available for this file type — download or open in a new tab to view.",
    onDownload: url ? () => window.open(url, "_blank", "noopener,noreferrer") : undefined,
    downloadDisabled: !url,
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-onyx/35 backdrop-blur-[1px]"
      role="presentation"
      onClick={onClose}
      data-testid="repository-preview-overlay"
    >
      <aside
        className="flex h-full w-full max-w-4xl animate-slide-in-right flex-col border-l-[2px] border-l-gold bg-white-pure shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="repository-preview-title"
        onClick={(event) => event.stopPropagation()}
        data-testid="repository-preview-drawer"
      >
        <header className="flex items-start justify-between gap-space-3 border-b border-border-light p-space-4">
          <div className="min-w-0 flex-1">
            <h2 id="repository-preview-title" className="m-0 truncate font-display text-h3 font-bold text-onyx">
              {doc?.title ?? "Document preview"}
            </h2>
            {doc && (
              <div className="mt-space-1 flex flex-wrap items-center gap-space-2 text-caption text-gray-mid">
                <Badge status="info">{doc.category}</Badge>
                <span>{doc.contentType}</span>
                <span>v{doc.version}</span>
                <RepositoryBadge status={doc.ingestionStatus} />
                {added && <span className="text-status-success">Selected ✓</span>}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-space-1 hover:bg-white-warm focus-visible:outline-none focus-visible:shadow-goldRing"
            aria-label="Close preview"
          >
            <X size={18} aria-hidden />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto bg-white-warm/35 p-space-4">
          <DocumentPreviewPane document={previewDocument} />
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-space-3 border-t border-border-light bg-white-pure p-space-4">
          <p className="m-0 text-caption text-gray-mid">
            Report conversion starts after selection and is shown in the selected-documents list.
          </p>
          <div className="flex items-center gap-space-2">
            {url && (
              <a href={url} target="_blank" rel="noreferrer" className="text-small font-semibold text-onyx underline hover:text-gold-shadow">
                Open in new tab
              </a>
            )}
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
            {added ? (
              <Button type="button" disabled>
                Selected ✓
              </Button>
            ) : selected && doc ? (
              <Button type="button" variant="secondary" onClick={() => onRemove(doc)}>
                Remove
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => doc && onSelect(doc)}
                disabled={adding || !!disabledReason}
                title={disabledReason ?? undefined}
              >
                {adding ? "Selecting…" : "Select document"}
              </Button>
            )}
          </div>
          {disabledReason && (
            <p className="basis-full m-0 text-caption text-status-warning" role="status">
              {disabledReason}
            </p>
          )}
        </footer>
      </aside>
    </div>
  );
}

function isRepositoryReady(doc: DocumentDto): boolean {
  return doc.ingestionStatus === "Completed" || doc.ingestionStatus === "Ingested";
}

// The repository "Ingested" status (KB index) is distinct from a report's
// conversion readiness — label it explicitly so it isn't mistaken for
// "ready for a report". Report-conversion status is shown on the selected
// list, where it actually gates Generate.
function RepositoryBadge({ status }: { status: IngestionStatus }) {
  if (status === "Completed" || status === "Ingested") {
    return (
      <span
        className="inline-flex items-center gap-space-1 text-caption text-status-success"
        title="Indexed in the document repository (separate from report conversion)."
      >
        <CheckCircle2 size={12} aria-hidden /> In repository
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-space-1 text-caption text-gray-mid">
      <Clock size={12} aria-hidden /> {status}
    </span>
  );
}
